import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase as supabaseClient } from '../../../lib/supabase';
import { projectService } from '../../../services/projectService';
import ContractorsVendorsSelector from './ContractorsVendorsSelector';
import { setProjectContractors, getProjectContractors } from '../../../services/contractorVendorLinkService';
import { toDateInputValue, parseLocalDate } from '../../../utils/dateUtils';

const CreateProjectModal = ({ isOpen, onClose, onSubmit, onUpdate, convertFromBid = null, editProject = null }) => {
  const isEditMode = !!editProject;
  const [formData, setFormData] = useState({
    projectName: '',
    customerName: '',
    totalBudget: '',
    startDate: '',
    endDate: '',
    location: '',
    description: '',
    status: 'Planning',
    convertedFromBid: false,
    bidNumber: '',
    assignedPmId: ''
  });
  const [customers, setCustomers] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultPhases, setDefaultPhases] = useState([]);
  const [applyDefaultPhases, setApplyDefaultPhases] = useState(false);
  const [selectedPhaseIds, setSelectedPhaseIds] = useState([]);
  const [phaseOverrides, setPhaseOverrides] = useState([]);
  const [showPhaseConfig, setShowPhaseConfig] = useState(false);

  // Contractor/Vendor selection state
  const [selectedSCIds, setSelectedSCIds] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadCustomers();
      loadPMUsers();
      loadDefaultPhases();
      if (isEditMode && editProject) {
        setFormData({
          projectName: editProject?.projectName || '',
          customerName: editProject?.customerName || '',
          totalBudget: editProject?.totalBudget?.toString() || '',
          startDate: editProject?.startDate ? toDateInputValue(editProject?.startDate) : '',
          endDate: editProject?.endDate ? toDateInputValue(editProject?.endDate) : '',
          location: editProject?.location || '',
          description: editProject?.description || '',
          status: editProject?.status || 'Planning',
          convertedFromBid: editProject?.convertedFromBid || false,
          bidNumber: editProject?.bidNumber || '',
          assignedPmId: editProject?.assignedPmId || editProject?.assigned_pm_id || ''
        });
        // Load existing contractor/vendor associations for edit mode
        loadProjectContractors(editProject?.id);
      } else if (convertFromBid) {
        setFormData({
          projectName: convertFromBid?.projectName || '',
          customerName: convertFromBid?.customerName || '',
          totalBudget: convertFromBid?.totalAmount?.toString() || '',
          startDate: '',
          endDate: '',
          location: convertFromBid?.projectLocation || '',
          description: convertFromBid?.description || '',
          status: 'Planning',
          convertedFromBid: true,
          bidNumber: convertFromBid?.bidNumber || '',
          assignedPmId: ''
        });
        setSelectedSCIds([]);
        setSelectedVendorIds([]);
      } else {
        setFormData({
          projectName: '',
          customerName: '',
          totalBudget: '',
          startDate: '',
          endDate: '',
          location: '',
          description: '',
          status: 'Planning',
          convertedFromBid: false,
          bidNumber: '',
          assignedPmId: ''
        });
        setSelectedSCIds([]);
        setSelectedVendorIds([]);
      }
      setErrors({});
    }
  }, [isOpen, convertFromBid, editProject]);

  const loadProjectContractors = async (projectId) => {
    if (!projectId) return;
    try {
      const data = await getProjectContractors(projectId);
      setSelectedSCIds(data?.subContractors?.map(c => c?.id) || []);
      setSelectedVendorIds(data?.vendors?.map(v => v?.id) || []);
    } catch (err) {
      console.error('Error loading project contractors:', err?.message);
    }
  };

  // Fetch customers from database
  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const { data, error } = await supabaseClient?.from('customers')?.select();
      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err?.message);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadPMUsers = async () => {
    try {
      const { data, error } = await supabaseClient
        ?.from('user_profiles')
        ?.select('id, full_name, first_name, last_name, nickname, email')
        ?.eq('is_pm', true)
        ?.eq('is_active', true)
        ?.order('full_name', { ascending: true });
      if (error) throw error;
      setPmUsers(data || []);
    } catch (err) {
      console.error('Error fetching PM users:', err?.message);
      setPmUsers([]);
    }
  };

  const loadDefaultPhases = async () => {
    try {
      const { data } = await projectService?.getDefaultPhases();
      setDefaultPhases(data || []);
      if (data?.length > 0) {
        setPhaseOverrides(data?.map(p => ({ ...p, startDate: '', endDate: '' })));
        setSelectedPhaseIds([]); // none selected by default
      }
    } catch (err) {
      console.error('Error loading default phases:', err?.message);
    }
  };

  // Convert customers to select options
  const customerOptions = customers?.map(customer => ({
    value: customer?.company_name,
    label: customer?.company_name
  }));

  const pmOptions = pmUsers?.map(pm => ({
    value: pm?.id,
    label: pm?.nickname || pm?.full_name || pm?.email
  }));

  const statusOptions = [
    { value: 'Planning', label: 'Planning' },
    { value: 'Bidding', label: 'Bidding' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'On Hold', label: 'On Hold' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleContractorChange = ({ subContractorIds, vendorIds }) => {
    setSelectedSCIds(subContractorIds);
    setSelectedVendorIds(vendorIds);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.projectName?.trim()) {
      newErrors.projectName = 'Project name is required';
    }

    if (!formData?.customerName) {
      newErrors.customerName = 'Customer selection is required';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData?.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData?.startDate && formData?.endDate && parseLocalDate(formData?.startDate) >= parseLocalDate(formData?.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!formData?.totalBudget || parseFloat(formData?.totalBudget) <= 0) {
      newErrors.totalBudget = 'Valid budget amount is required';
    }

    if (!formData?.location?.trim()) {
      newErrors.location = 'Project location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const projectData = {
          projectName: formData?.projectName,
          customerName: formData?.customerName,
          status: formData?.status,
          totalBudget: parseFloat(formData?.totalBudget),
          startDate: formData?.startDate,
          endDate: formData?.endDate,
          location: formData?.location,
          description: formData?.description || '',
          convertedFromBid: formData?.convertedFromBid,
          bidNumber: formData?.bidNumber || null,
          conversionDate: formData?.convertedFromBid ? (editProject?.conversionDate || new Date()?.toISOString()?.split('T')?.[0]) : null,
          costBreakdown: editProject?.costBreakdown || [],
          assignedPmId: formData?.assignedPmId || null
        };

        if (isEditMode) {
          await onUpdate(editProject?.id, projectData);
          // Save contractor/vendor associations
          await setProjectContractors(editProject?.id, {
            subContractorIds: selectedSCIds,
            vendorIds: selectedVendorIds,
          });
        } else {
          projectData.completionPercentage = 0;
          projectData.actualCost = 0;
          projectData.variance = 0;
          const result = await onSubmit(projectData);
          // Apply selected phases only if user explicitly chose them
          if (selectedPhaseIds?.length > 0 && result?.id) {
            const phasesToApply = phaseOverrides
              ?.filter(p => selectedPhaseIds?.includes(p?.id))
              ?.map((p, i) => ({
                project_id: result?.id,
                name: p?.name,
                description: p?.description || '',
                status: 'Planning',
                progress: 0,
                start_date: p?.startDate || formData?.startDate || new Date()?.toISOString()?.split('T')?.[0],
                end_date: p?.endDate || formData?.endDate || new Date()?.toISOString()?.split('T')?.[0],
                deliverable_count: 0,
                sort_order: i + 1
              }));
            await supabaseClient?.from('project_phases')?.insert(phasesToApply);
          }
          // Save contractor/vendor associations for new project
          if (result?.id) {
            await setProjectContractors(result?.id, {
              subContractorIds: selectedSCIds,
              vendorIds: selectedVendorIds,
            });
          }
        }
      } catch (error) {
        console.error('Error submitting project:', error);
        alert(isEditMode ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name={isEditMode ? 'Edit' : 'FolderPlus'} size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">{isEditMode ? 'Edit Project' : 'Create New Project'}</h2>
              <p className="text-sm text-muted-foreground">{isEditMode ? 'Update project details' : 'Add a new construction project'}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4 md:space-y-6">
            <Input
              label="Project Name"
              type="text"
              placeholder="Enter project name"
              value={formData?.projectName}
              onChange={(e) => handleChange('projectName', e?.target?.value)}
              error={errors?.projectName}
              required
            />

            <Select
              label="Customer"
              options={customerOptions}
              value={formData?.customerName}
              onChange={(value) => handleChange('customerName', value)}
              placeholder={loadingCustomers ? "Loading customers..." : "Select customer"}
              error={errors?.customerName}
              required
              searchable
              disabled={loadingCustomers}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleChange('startDate', e?.target?.value)}
                error={errors?.startDate}
                required
              />

              <Input
                label="End Date"
                type="date"
                value={formData?.endDate}
                onChange={(e) => handleChange('endDate', e?.target?.value)}
                error={errors?.endDate}
                required
              />
            </div>

            <Input
              label="Total Budget"
              type="number"
              placeholder="Enter total budget"
              value={formData?.totalBudget}
              onChange={(e) => handleChange('totalBudget', e?.target?.value)}
              error={errors?.totalBudget}
              required
              description="Enter the total project budget in USD"
            />

            <Input
              label="Project Location"
              type="text"
              placeholder="Enter project location"
              value={formData?.location}
              onChange={(e) => handleChange('location', e?.target?.value)}
              error={errors?.location}
              required
            />

            <Select
              label="Initial Status"
              options={[
                { value: 'Planning', label: 'Planning' },
                { value: 'Bidding', label: 'Bidding' },
                { value: 'In Progress', label: 'In Progress' },
                { value: 'On Hold', label: 'On Hold' },
                { value: 'Completed', label: 'Completed' },
                { value: 'Cancelled', label: 'Cancelled' }
              ]}
              value={formData?.status}
              onChange={(value) => handleChange('status', value)}
              placeholder="Select status"
            />

            <Select
              label="Assigned PM"
              options={pmOptions}
              value={formData?.assignedPmId}
              onChange={(value) => handleChange('assignedPmId', value)}
              placeholder={pmUsers?.length === 0 ? 'No PM users available' : 'Select project manager'}
              disabled={pmUsers?.length === 0}
            />

            {/* Contractors & Vendors */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Contractors &amp; Vendors
              </label>
              <ContractorsVendorsSelector
                selectedSCIds={selectedSCIds}
                selectedVendorIds={selectedVendorIds}
                onChange={handleContractorChange}
              />
            </div>

            {/* Default Phases Section - only for new projects */}
            {!isEditMode && defaultPhases?.length > 0 && (
              <div className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="GitBranch" size={16} className="text-accent" />
                    <span className="text-sm font-medium text-foreground">Add Default Phases</span>
                    <span className="text-xs text-muted-foreground">(optional)</span>
                  </div>
                  {selectedPhaseIds?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowPhaseConfig(prev => !prev)}
                      className="text-xs text-accent hover:underline flex items-center gap-1"
                    >
                      {showPhaseConfig ? 'Hide dates' : 'Configure dates'}
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {defaultPhases?.map(phase => {
                    const isSelected = selectedPhaseIds?.includes(phase?.id);
                    const override = phaseOverrides?.find(p => p?.id === phase?.id);
                    return (
                      <div key={phase?.id} className="space-y-1">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e?.target?.checked) {
                                setSelectedPhaseIds(prev => [...prev, phase?.id]);
                              } else {
                                setSelectedPhaseIds(prev => prev?.filter(id => id !== phase?.id));
                              }
                            }}
                            className="w-4 h-4 rounded border-border"
                          />
                          <span className={`text-sm font-medium transition-colors ${isSelected ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}>
                            {phase?.name}
                          </span>
                          {phase?.description && (
                            <span className="text-xs text-muted-foreground truncate">{phase?.description}</span>
                          )}
                        </label>
                        {isSelected && showPhaseConfig && override && (
                          <div className="ml-6 grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                              <input
                                type="date"
                                value={override?.startDate || formData?.startDate || ''}
                                onChange={(e) => setPhaseOverrides(prev => prev?.map(p => p?.id === phase?.id ? { ...p, startDate: e?.target?.value } : p))}
                                className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                              <input
                                type="date"
                                value={override?.endDate || formData?.endDate || ''}
                                onChange={(e) => setPhaseOverrides(prev => prev?.map(p => p?.id === phase?.id ? { ...p, endDate: e?.target?.value } : p))}
                                className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedPhaseIds?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedPhaseIds?.length} phase{selectedPhaseIds?.length !== 1 ? 's' : ''} will be added to the project
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Project Description
              </label>
              <textarea
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                rows={4}
                placeholder="Enter project description"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName={isEditMode ? 'Save' : 'Plus'}
            iconPosition="left"
            className="w-full sm:w-auto"
            disabled={loadingCustomers || isSubmitting}
          >
            {isEditMode ? 'Save Changes' : 'Create Project'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;